import { asAmount } from "@goodfill/domain-properties";
import { MinimumInvestmentComponent } from "@goodfill/domain-properties-components";
import { AccordionSection, Flex, InputCheckbox, Label } from "@goodfill/ui-components";
import Decimal from "decimal.js";
import { useCallback, useState } from "react";
import {
  getSessionStorageBoolean,
  removeItemFromSessionStorage,
  saveBooleanToSessionStorage,
} from "../../../../../sessionStorage";
import { PricedBond } from "../../../PricedBond";
import { BondFilter, BondFilterChipData, BondFilterData, BondFilterProps } from "../BondFilter";
import { BondFilterSectionGap, BondFilterSectionMargin } from "../BondFilterConst";
import { BondFilterCriteria } from "../BondFilterCriteria";
import { BondFilterDataConstraints } from "../BondFilterDataConstraints";

const FILTER_MINIMUM_AMOUNT_1000_STORAGE_KEY = "filter:minimumAmount";

const _Component = (props: BondFilterProps) => {
  const setAmount1000 = useCallback(
    (value: boolean) => {
      props.onChange({
        criteria: {
          ...props.criteria,
          minimumAmount1000: value,
        },
      });
    },
    [props],
  );

  const [sectionIsOpen, setSectionIsOpen] = useState(false);

  const checked = props.criteria.minimumAmount1000;

  return (
    <AccordionSection title={"Minimum Investment Amount"} isOpen={sectionIsOpen} setIsOpen={setSectionIsOpen}>
      <Flex direction="col" verticalMargin={BondFilterSectionMargin} gap={BondFilterSectionGap}>
        <Flex key={"filterMinimumInvestment"} direction="row" items="center" gap={3}>
          <InputCheckbox
            id={"filterMinimumInvestment"}
            checked={checked}
            onChange={() => setAmount1000(!props.criteria.minimumAmount1000)}
          />
          <Label uppercase={true} htmlFor={"filterMinimumInvestment"} accent={!checked} primary={checked}>
            Minimum Amount <MinimumInvestmentComponent value={asAmount(new Decimal(1000))} />
          </Label>
        </Flex>
      </Flex>
    </AccordionSection>
  );
};

export class BondFilterMinimumAmount implements BondFilter {
  isVisibleInDropdown(): boolean {
    return true;
  }

  reset(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterCriteria {
    return {
      ...criteria,
      minimumAmount1000: defaultCriteria.minimumAmount1000,
    };
  }

  getChips(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterChipData[] {
    return criteria.minimumAmount1000 !== defaultCriteria.minimumAmount1000
      ? [
          {
            id: "minimumAmount",
            label: "Minimum Investment Amount",
            onClick: () => this.reset(criteria, defaultCriteria),
          },
        ]
      : [];
  }

  evaluate(data: BondFilterData, criteria: BondFilterCriteria): boolean {
    if (criteria.minimumAmount1000) {
      return data.pricedBond.minimumPiece.amount.lessThanOrEqualTo(1000);
    }

    return true;
  }

  updateConstraintByPricedBond(
    _pricedBond: PricedBond,
    current: BondFilterDataConstraints,
  ): BondFilterDataConstraints {
    return current;
  }

  getDefaultCriteriaConstraints(_pricedBond: PricedBond): Partial<BondFilterDataConstraints> {
    return {};
  }

  afterCriteriaCalculation(current: BondFilterDataConstraints): BondFilterDataConstraints {
    return current;
  }

  component = _Component;

  applyCriteriaFromStorage(criteria: Partial<BondFilterCriteria>): Partial<BondFilterCriteria> {
    return {
      ...criteria,
      minimumAmount1000: getSessionStorageBoolean(FILTER_MINIMUM_AMOUNT_1000_STORAGE_KEY),
    };
  }
  storeCriteriaToStorage(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): void {
    if (criteria.minimumAmount1000 === defaultCriteria.minimumAmount1000) {
      removeItemFromSessionStorage(FILTER_MINIMUM_AMOUNT_1000_STORAGE_KEY);
    } else {
      saveBooleanToSessionStorage(FILTER_MINIMUM_AMOUNT_1000_STORAGE_KEY, criteria.minimumAmount1000);
    }
  }
}
